/**
 * The landing page's translation layer.
 *
 * The same design as the console's `web/src/i18n/index.ts`, written out here
 * rather than imported. Not sharing it is deliberate: the console's catalogue is
 * 2,356 lines of operator vocabulary, and importing the module would drag the
 * whole dictionary into this bundle and make the console's own dead-key counter
 * (`keys.test.ts`, `DEBT = 475`) meaningless — this page would suddenly "read"
 * arbitrary console keys.
 *
 * What is kept is the part that matters: Turkish is written first and is the
 * source of truth, English is typed against it, and a flat record makes a
 * missing key a compile error rather than an `undefined` rendered into the page.
 *
 * What is dropped, because a marketing page renders no machine values and has no
 * stored preference (the locale is in the URL): `loadLocale`/`saveLocale`,
 * `DISPLAY_TIMEZONE`, and every `format*` helper.
 */

import { tr } from './tr'
import { en } from './en'

export type Locale = 'tr' | 'en'

/** Every locale this page is built for, in the order the sitemap lists them. */
export const LOCALES: readonly Locale[] = ['tr', 'en'] as const

/** The one the apex redirects to when a visitor expresses no preference. */
export const DEFAULT_LOCALE: Locale = 'tr'

/** Every translatable string key. Derived from the Turkish catalogue, which is
 *  authoritative — adding a key to `en` that `tr` lacks will not compile. */
export type MessageKey = keyof typeof tr

const catalogues: Record<Locale, Record<MessageKey, string>> = { tr, en }

/**
 * Translates a key, substituting `{name}` placeholders.
 *
 * A key with no entry in the active catalogue falls back to Turkish rather than
 * to the raw key, so a partially translated English catalogue degrades into a
 * readable mixed page instead of printing `hero.title` at a visitor.
 */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const template = catalogues[locale][key] ?? tr[key] ?? String(key)
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

/** Narrows a runtime string to a known locale. Used by the entry points, which
 *  read what the prerender stamped rather than parsing the path — one source at
 *  runtime, because two would eventually disagree. */
export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'tr' || value === 'en'
}
